import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators, FormArray } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { delay, map, mergeMap, take } from 'rxjs/operators';
import { UserLocalStorage } from '../../login/user-local-store';
import { v4 } from 'uuid';
import { PackagingItem } from '../controllers/controller.model';
import { CopyButtonComponent } from '../copy-button/copy-button.component';
import { DisplayProductDataService } from '../display-product-data.service';
import { checkAccessoryName } from '../names.validator';
import { Accessory, ACCESSORY_ITEMS } from '../pump-data.model';
import { PackagingState } from '../store/packaging/packaging.reducer';
import { selectPackaging } from '../store/packaging/packaging.selector';
import { selectAccessory } from '../store/accessory/accessory.selector';
import { AccessoryState } from '../store/accessory/accessory.reducer';

@Component({
    selector: 'app-accessory-new',
    templateUrl: './accessory-new.component.html',
    styleUrls: ['./accessory-new.component.scss'],
    standalone: false
})
export class AccessoryNewComponent implements OnInit, OnDestroy {

  private subscriptions: Subscription[] = [];
  private formValueChange: Subscription = new Subscription;
  isDirty = false;
  originalAccessoryFormValue: any;

  userLocalStorage = new UserLocalStorage();
  accessoryForm: UntypedFormGroup;

  selectedRowIndex = -1;
  showLoading = false;
  copiedItem = '';
  accessoryData = [];
  packagingList = [];
  accessoryList: Accessory[] = [];
  myAccessory: any = {};
  overview: any;
  secondary_accessory: any;

  constructor(
    private store: Store,
    private router: Router,
    private fb: UntypedFormBuilder,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private displayProductData: DisplayProductDataService
  ) { }

  ngOnInit() {
    this.initRecalculateForm();

    const getPackagingList = this.store.pipe(
      select(selectPackaging),
      map((data: PackagingState) => data.data)
    ).subscribe((data: PackagingItem[]) => {
      this.packagingList = [...data].sort((a, b) => Number(a.subType.L) - Number(b.subType.L));
    });

    const getAccessoryList = this.store.pipe(
      select(selectAccessory)
    ).subscribe((list: AccessoryState) => {
      this.accessoryList = list.accessories;
    });

    this.subscriptions.push(getPackagingList);
    this.subscriptions.push(getAccessoryList);
  }

  openCopyDialog(type: string) {
    const dialogRef = this.dialog.open(CopyButtonComponent, {
      width: '1000px',
      maxWidth: '96vw',
      data: type,
      minHeight: '60vh',
      maxHeight: '60vh',
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.showLoading = true;
        this.copyItem(result);
      }
    });
  }

  copyItem(accessoryUuid: string) {
    this.displayProductData.getAccessoryItem(accessoryUuid)
      .subscribe((accessory: any) => {
        this.showLoading = false;
        this.copiedItem = accessory.name;
        const accessoryData = accessory.versions[0];
        this.mapAccessoryToForm(accessoryData);
      });
  }

  private mapAccessoryToForm(accessoryData: any): void {
  // ---- 1. Patch simple scalar fields ----
  this.accessoryForm.patchValue({
    name: accessoryData.name ?? '',
    type: accessoryData.type ?? '',
    status: accessoryData.status ?? '',
    category: accessoryData.category ?? '',
    item_no_1: accessoryData.item_no_1 ?? '',
    product_name_1: accessoryData.product_name_1 ?? '',
    packaging: accessoryData.packaging ?? null,
    controller: accessoryData.controller ?? [],
    pump_family: accessoryData.pump_family ?? [],
    pump_type: accessoryData.pump_type ?? [],
    comment: accessoryData.comment ?? '',
    secondary_accessory: accessoryData.secondary_accessory ?? false,
    secondary_to: accessoryData.secondary_to ?? []
  });

  // ---- 2. extraAttributes → FormArray ----
  const attributes = accessoryData.extraAttributes ?? [];

  const formArray = this.fb.array(
    attributes.map(attr =>
      this.fb.group({
        field: [attr?.field ?? ''],
        value: [attr?.value ?? '']
      })
    )
  );

  this.accessoryForm.setControl('extraAttributes', formArray);

  // ---- 3. overview → standalone binding ----
  this.overview = accessoryData.overview ?? {};

  // ---- 4. Keep internal reference clean ----
  this.myAccessory = {
    ...accessoryData,
    overview: this.overview
  };

  // ---- 5. Reset dirty tracking baseline ----
  this.originalAccessoryFormValue = this.accessoryForm.getRawValue();
}

private mapFormToAccessory(): any {
  const formValue = this.accessoryForm.getRawValue();

  return {
    ...formValue,

    // ---- normalize extraAttributes ----
    extraAttributes: (formValue.extraAttributes || [])
      .filter(attr => attr.field || attr.value),

    // ---- overview comes from editor ----
    overview: this.myAccessory.overview ?? {},

    // ---- normalize booleans / arrays ----
    secondary_accessory: !!formValue.secondary_accessory,
    secondary_to: formValue.secondary_to ?? []
  };
}

  initRecalculateForm() {
    const data: any = {};
    ACCESSORY_ITEMS.forEach(name => {
      data[`${name}`] = [null];
    });
    data.name = ['', [Validators.required, checkAccessoryName.bind(this)]];
    data.packaging = [null, [Validators.required]];
    data.type = [null, [Validators.required]];
    data.status = [null, [Validators.required]];
    data.category = [null, [Validators.required]];
    data.item_no_1 = [null, [Validators.required]];
    data.product_name_1 = [null, [Validators.required]];
    this.accessoryForm = this.fb.group({...data,
        extraAttributes: this.fb.array([]),
      secondary_accessory:false,
      overview:{},}
      );
  }

  onSubmit() {
    const data = this.mapFormToAccessory();
    const user = this.userLocalStorage.loadUser();
    data.author = user.user.username;
    data.uuid = v4();
    data.file = this.accessoryForm.value.name;
    data.preview = true;

    if ( this.accessoryForm.value.packaging && this.accessoryForm.value.packaging.subType ) {
      const packagingValue: any = {};
      packagingValue.name = data.packaging.uuid;
      packagingValue.w = data.packaging.subType.W;
      packagingValue.h = data.packaging.subType.H;
      packagingValue.l = data.packaging.subType.L;
      packagingValue.pack_weight = data.packaging.weight;
      data.packaging = packagingValue;
    } else {
      const result = this.packagingList.filter(item => item.name.includes(data.packaging));
      if (result && result.length > 0) {
        const packaging: any = {};
        packaging.name = result[0].uuid;
        packaging.w = `${result[0].subType.W}`;
        packaging.h = `${result[0].subType.H}`;
        packaging.l = `${result[0].subType.L}`;
        packaging.pack_weight = `${result[0].weight}`;
        data.packaging = packaging;
      }
    }

    this.displayProductData.getAccessoryList()
      .pipe(
        take(1),
        mergeMap(allAccessories => {
          data.id = allAccessories.length;
          return this.displayProductData.createAccessoryItem(data);
        })
      ).subscribe(item => {
        this.snackBar
          .open(item, '', { duration: 1500, panelClass: 'hintMsg' })
          .afterDismissed()
          .pipe(delay(0))
          .subscribe(() => {
            const url = `${data.uuid}/1/preview`;
            this.displayProductData.reloadAccessoryData();
            this.displayProductData.setAccessoryState(url, data).subscribe(item => {
              this.router.navigate(['/data/accessory', data.uuid]); });

          });
      });
  }
  
  onOverviewChange(updatedOverview: any): void {
    const originalOverview = this.myAccessory.overview || {};
    const overviewChanged = JSON.stringify(originalOverview) !== JSON.stringify(updatedOverview);

    this.myAccessory.overview = updatedOverview;

    if (this.accessoryForm.contains('overview')) {
      this.silentlyUpdateForm(() => {
        this.accessoryForm.patchValue({ overview: updatedOverview });
      });
    }

    if (overviewChanged) {
      this.isDirty = true;
    }
  }

  get extraAttributes() {
    return this.accessoryForm.get('extraAttributes') as FormArray;
  }

  set extraAttributes(value: FormArray) {
    this.accessoryForm.setControl('extraAttributes', value);
  }

  removeExtraAttribute(index: number) {
    this.silentlyUpdateForm(() => {
      this.extraAttributes.removeAt(index);
    });
  }

  addExtraAttribute() {
    this.silentlyUpdateForm(() => {
      this.extraAttributes.push(
        this.fb.group({
          field: [''],
          value: ['']
        })
      );
    });
  }

  private silentlyUpdateForm(updateFn: () => void) {
    this.formValueChange.unsubscribe();   // stop listening
    updateFn();                           // perform the update
    this.formValueChange = this.accessoryForm.valueChanges.subscribe(value => {
      this.isDirty =
        JSON.stringify(this.originalAccessoryFormValue) !== JSON.stringify(value);
    });
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
